import {useTranslation} from '@/translations/i18n'
import {formatCurrency} from '@/utilities/helpers'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {DashboardEconomicSearchParams} from '../../types'
import {useGetDashboardAnalytics} from '../../query/useGetDashboardAnalytics'
import {useMemo} from 'react'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {StyledOtherCostsDataWrapper, StyledPrimaryDataContainer, StyledSecondaryDataContainer} from './style'
import {Divider} from '@/components/ui/divider/Divider'
import {DashboardEconomicSummaryCardSkeleton} from './DashboardEconomicSummaryCardSkeleton'
import {calculateFirstMargin, calculateOperatingMargin} from '../../utils'

export const DashboardEconomicSummaryCard = () => {
    const {t} = useTranslation()
    const {searchParams} = useParsedSearchParams(DashboardEconomicSearchParams)
    const {data, isLoading} = useGetDashboardAnalytics({
        fields: ['billedAccesses', 'costs', 'otherCosts'],
        ...searchParams
    })

    const firstMargin = useMemo(() => {
        return calculateFirstMargin(data?.billedAccesses?.accesses, data?.costs?.total)
    }, [data?.billedAccesses?.accesses, data?.costs?.total])

    const operatingMargin = useMemo(() => {
        return calculateOperatingMargin(firstMargin, data?.otherCosts?.total)
    }, [firstMargin, data?.otherCosts?.total])

    if (isLoading) {
        return <DashboardEconomicSummaryCardSkeleton />
    }

    return (
        <Flexbox direction="column" gap={4} fullWidth>
            <StyledOtherCostsDataWrapper direction="column" gap={2} fullWidth>
                <StyledSecondaryDataContainer justify="space-between" align="center" fullWidth>
                    <Text palette="neutral" shade="900" size="textMd" weight={500}>
                        {t('dashboard:reimbursed_accesses')}
                    </Text>
                    <Text palette="neutral" shade="900" size="textLg" weight={500}>
                        {formatCurrency(data?.billedAccesses?.accesses ?? 0)}
                    </Text>
                </StyledSecondaryDataContainer>
                <StyledSecondaryDataContainer justify="space-between" align="center" fullWidth>
                    <Text palette="neutral" shade="900" size="textMd" weight={500}>
                        {t('dashboard:calculated_costs')}
                    </Text>
                    <Text palette="neutral" shade="900" size="textLg" weight={500}>
                        {formatCurrency(data?.costs?.total ?? 0)}
                    </Text>
                </StyledSecondaryDataContainer>
            </StyledOtherCostsDataWrapper>

            <Divider direction="horizontal" />

            <StyledPrimaryDataContainer justify="space-between" align="center" fullWidth>
                <Text palette="neutral" shade="900" size="textLg" weight={500}>
                    {t('dashboard:first_margin')}
                </Text>
                <Text palette="neutral" shade="900" size="displaySm" weight={500}>
                    {formatCurrency(firstMargin)}
                </Text>
            </StyledPrimaryDataContainer>

            <Divider direction="horizontal" />

            <StyledOtherCostsDataWrapper fullWidth>
                <StyledSecondaryDataContainer justify="space-between" align="center" fullWidth>
                    <Text palette="neutral" shade="900" size="textMd" weight={500}>
                        {t('dashboard:other_costs')}
                    </Text>
                    <Text palette="neutral" shade="900" size="textLg" weight={500}>
                        {formatCurrency(data?.otherCosts?.total ?? 0)}
                    </Text>
                </StyledSecondaryDataContainer>
            </StyledOtherCostsDataWrapper>

            <Divider direction="horizontal" />

            <StyledPrimaryDataContainer justify="space-between" align="center" fullWidth>
                <Text palette="neutral" shade="900" size="textLg" weight={500}>
                    {t('dashboard:operating_margin')}
                </Text>
                <Text palette="neutral" shade="900" size="displaySm" weight={500}>
                    {formatCurrency(operatingMargin)}
                </Text>
            </StyledPrimaryDataContainer>
        </Flexbox>
    )
}
